import React from 'react';
import { WebsiteDesignProps } from './types';
import { Button } from "@/components/ui/button";
import { Search, Menu, GraduationCap, Heart, Trophy, Atom, ArrowRight } from 'lucide-react';

const DukeUniversity: React.FC<WebsiteDesignProps> = ({ previewData }) => {
    const schoolName = previewData?.name || "Duke University";

    return (
        <div className="min-h-screen bg-white font-sans text-[#001A57] selection:bg-[#001A57] selection:text-white">
            {/* Top Bar - Duke Blue */}
            <div className="bg-[#001A57] text-white py-3 px-6 flex justify-between items-center text-sm font-bold">
                <div className="flex gap-4">
                    <a href="#" className="hover:underline opacity-80 hover:opacity-100">Students</a>
                    <a href="#" className="hover:underline opacity-80 hover:opacity-100">Faculty & Staff</a>
                    <a href="#" className="hover:underline opacity-80 hover:opacity-100">Alumni</a>
                    <a href="#" className="hover:underline opacity-80 hover:opacity-100">Parents</a>
                </div>
                <div className="flex gap-4 opacity-80 hover:opacity-100 cursor-pointer">
                    <Search size={18} />
                </div>
            </div>

            {/* Header w/ Logo */}
            <div className="border-b border-gray-200 py-6 px-6 sticky top-0 bg-white/95 backdrop-blur z-50">
                <div className="max-w-[1400px] mx-auto flex justify-between items-center">
                    <h1 className="font-serif text-4xl text-[#001A57] tracking-tight">
                        {schoolName}
                    </h1>
                    
                    <div className="hidden lg:flex gap-8 font-bold text-[#001A57] uppercase text-sm tracking-widest">
                        <a href="#" className="hover:text-[#00539B] transition-colors">About</a>
                        <a href="#" className="hover:text-[#00539B] transition-colors">Admissions</a>
                        <a href="#" className="hover:text-[#00539B] transition-colors">Academics</a>
                        <a href="#" className="hover:text-[#00539B] transition-colors">Research</a>
                        <a href="#" className="hover:text-[#00539B] transition-colors">Health System</a>
                        <a href="#" className="hover:text-[#00539B] transition-colors">Athletics</a>
                    </div>

                    <div className="lg:hidden">
                        <Menu size={28} className="text-[#001A57]" />
                    </div>
                </div>
            </div>

            {/* Hero Section - Gothic Architecture background */}
            <header className="relative h-[650px] overflow-hidden">
                <img 
                    src="https://images.unsplash.com/photo-1564981797816-1043664bf78d?q=80&w=2070&auto=format&fit=crop" 
                    alt="Duke Chapel" 
                    className="absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-[#001A57]/60 mix-blend-multiply"></div>
                <div className="absolute inset-0 bg-gradient-to-t from-[#001A57] via-transparent to-transparent"></div>
                
                <div className="absolute bottom-0 w-full p-12 md:p-24 text-center">
                    <h1 className="text-5xl md:text-7xl font-bold text-white mb-8 font-serif">
                        Knowledge in the <br/> Service of Society.
                    </h1>
                    <div className="flex justify-center gap-4">
                        <Button className="bg-[#00539B] hover:bg-white hover:text-[#001A57] text-white font-bold uppercase tracking-widest px-8 h-12 rounded-sm transition-colors border border-[#00539B]">
                            Read Our Strategic Plan
                        </Button>
                    </div>
                </div>
            </header>

            {/* Content Modules */}
            <section className="py-24 px-6 md:px-12 bg-[#F3F2F1]">
                <div className="max-w-[1400px] mx-auto grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
                    <div>
                        <span className="text-[#00539B] font-bold uppercase tracking-widest text-sm mb-4 block">Student Experience</span>
                        <h2 className="text-4xl font-serif text-[#001A57] mb-6">A Community of Scholars</h2>
                        <p className="text-lg text-gray-700 leading-relaxed mb-8">
                            {schoolName.split(' ')[0]} undergraduate students have opportunities to combine their interests in unique ways, conducting research alongside faculty, engaging in the community, and cheering on the Blue Devils.
                        </p>
                        <a href="#" className="text-[#00539B] font-bold flex items-center gap-2 hover:gap-4 transition-all">
                            Explore Student Life <ArrowRight size={18} />
                        </a>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <img src="https://images.unsplash.com/photo-1523240795612-9a054b0db644?q=80&w=2070&auto=format&fit=crop" className="w-full aspect-square object-cover rounded-sm" alt="Students" />
                        <img src="https://images.unsplash.com/photo-1629904853716-6b031b0728cc?q=80&w=2070&auto=format&fit=crop" className="w-full aspect-square object-cover rounded-sm translate-y-8" alt="Basketball" />
                    </div>
                </div>
            </section>

            {/* Impact Icons */}
            <section className="bg-white py-24 px-6 md:px-12">
                <div className="max-w-[1200px] mx-auto grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-12 text-center">
                    {[
                        { icon: Heart, title: "Health", desc: "Revolutionizing patient care and discovery." },
                        { icon: Atom, title: "Energy", desc: "Solving climate challenges for tomorrow." },
                        { icon: Trophy, title: "Excellence", desc: "27 Nobel laureates affiliated with Duke." },
                        { icon: GraduationCap, title: "Access", desc: "Support for first-gen students." }
                    ].map((item, i) => (
                        <div key={i} className="group p-6 hover:bg-[#F3F2F1] rounded-lg transition-colors cursor-pointer">
                            <div className="w-16 h-16 bg-[#001A57] rounded-full flex items-center justify-center mx-auto mb-6 text-white group-hover:scale-110 transition-transform">
                                <item.icon size={32} />
                            </div>
                            <h3 className="text-xl font-bold text-[#001A57] mb-3">{item.title}</h3>
                            <p className="text-gray-600 text-sm leading-relaxed">{item.desc}</p>
                        </div>
                    ))}
                </div>
            </section>

            {/* Footer */}
            <footer className="bg-[#121212] text-white py-16 px-6 md:px-12 border-t-8 border-[#001A57]">
                <div className="max-w-[1400px] mx-auto flex flex-col md:flex-row justify-between gap-12">
                    <div className="md:w-1/3">
                        <h2 className="font-serif text-3xl mb-6">{schoolName.split(' ')[0]}</h2>
                        <p className="text-gray-400 text-sm mb-6">
                            {schoolName} <br/>
                            Durham, NC 27708 <br/>
                            (919) 684-8111
                        </p>
                        <div className="flex gap-4">
                             {/* Socials Placeholders */}
                             <div className="w-8 h-8 bg-gray-700 rounded-full hover:bg-[#00539B] transition-colors"></div>
                             <div className="w-8 h-8 bg-gray-700 rounded-full hover:bg-[#00539B] transition-colors"></div>
                             <div className="w-8 h-8 bg-gray-700 rounded-full hover:bg-[#00539B] transition-colors"></div>
                        </div>
                    </div>

                    <div className="md:w-2/3 grid grid-cols-2 md:grid-cols-3 gap-8 text-sm font-bold text-gray-300">
                        <ul className="space-y-4">
                            <li><a href="#" className="hover:text-white">Apply</a></li>
                            <li><a href="#" className="hover:text-white">Visit</a></li>
                            <li><a href="#" className="hover:text-white">Give</a></li>
                            <li><a href="#" className="hover:text-white">Careers</a></li>
                        </ul>
                        <ul className="space-y-4">
                            <li><a href="#" className="hover:text-white">Contact {schoolName.split(' ')[0]}</a></li>
                            <li><a href="#" className="hover:text-white">{schoolName.split(' ')[0]} Health</a></li>
                            <li><a href="#" className="hover:text-white">Blue Devils</a></li>
                            <li><a href="#" className="hover:text-white">{schoolName.split(' ')[0]} Alumni</a></li>
                        </ul>
                         <ul className="space-y-4">
                            <li><a href="#" className="hover:text-white">Privacy</a></li>
                            <li><a href="#" className="hover:text-white">Accessibility</a></li>
                            <li><a href="#" className="hover:text-white">Terms</a></li>
                        </ul>
                    </div>
                </div>
                <div className="max-w-[1400px] mx-auto mt-12 pt-8 border-t border-gray-800 text-center text-xs text-gray-500">
                    © 2026 {schoolName}
                </div>
            </footer>
        </div>
    );
};

export default DukeUniversity;
